function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/* Import External Modules */
import React from 'react';
import { connect } from 'react-redux';

/* Import Leverege Modules */
import { ApiProvider } from '@leverege/api-redux';
import { Pane, Toast } from '@leverege/ui-elements';
import { v4 } from '@leverege/base62-util';
import I18N from '@leverege/i18n';

/* Import Local Modules */
import DefaultRoleSelector from "../role/RoleSelector.js";
import UserSelector from "./UserSelector.js";
import NewUserForm from "./NewUserForm.js";
import S from './User.css';
const {
  t
} = I18N.ns('userManagement.user.AddEditUserEditor');

/**
 * User editor.
 */
class AddEditUserEditor extends React.Component {
  /* Initialize Static Variables */
  static contextType = ApiProvider.Context;

  /**
   * Constructs component.
   */
  constructor(props) {
    super(props);
    this.state = {
      newUserFormKey: Date.now(),
      existingUsers: []
    };
  }

  /**
   * Runs once when component mounts to initialize edit-user information.
   */
  async componentDidMount() {
    const {
      edit,
      user
    } = this.props;
    if (edit) {
      await this.getRoles(user);
      return this.setState({
        user,
        userExists: true,
        selectionType: 'update'
      });
    }
  }

  /**
   * Sends user changes to user change handler function prop.
   */
  sendChange = () => {
    const {
      onChange,
      eventData
    } = this.props;
    const {
      roles,
      userId,
      user,
      originalRoles,
      selectionType
    } = this.state;
    const value = userId != null ? {
      userId,
      user,
      roles,
      originalRoles,
      selectionType
    } : {
      user,
      roles,
      originalRoles,
      selectionType
    };
    onChange({
      data: eventData,
      value
    });
  };

  /** 
   * Gets available roles for a user.
   */
  async getRoles(user) {
    // Get parameters
    const {
      dispatch,
      actions
    } = this.props;

    // Validate parameters
    if (!actions) {
      return this.setState({
        userId: user.id,
        user,
        originalRoles: [],
        roles: [],
        userExists: false
      }, this.sendChange);
    }

    // Get roles and update state
    try {
      const roleList = await dispatch(actions.user(user.id).roles());
      const roles = roleList.items;
      const originalRoles = roles;
      this.setState({
        userId: user.id,
        user,
        originalRoles,
        roles,
        userExists: roles.length > 0
      }, this.sendChange);
    } catch (error) {
      Toast.error([t('toastError'), error.message]);
      return this.setState({
        userId: user.id,
        user,
        originalRoles: [],
        roles: [],
        userExists: false
      }, this.sendChange);
    }
  }

  /**
   * Handles role change events.
   */
  onRoleChange = event => {
    this.setState({
      roles: event.value
    }, this.sendChange);
  };

  /**
   * Handles local update events.
   */
  onLocalUpdate = values => {
    const {
      user
    } = this.state;
    this.setState({
      user: {
        ...user,
        ...values
      }
    }, this.sendChange);
  };

  /**
   * Handles user selection events.
   */
  onSelectUser = async event => {
    // Get parameters
    const data = event.value;

    // Validate parameters
    if (data == null) {
      return this.setState({
        userId: null,
        user: data,
        originalRoles: [],
        roles: [],
        userExists: false,
        selectionType: null
      }, this.sendChange);
    }

    // Get more parameters
    const {
      setDialogTitle,
      setDialogOkayLabel
    } = this.props;
    const {
      selectionType,
      ...user
    } = data || {};
    const {
      existingUsers
    } = this.state;
    const isExisting = selectionType === 'update' || selectionType === 'apiAccess';

    // Update dialog view
    setDialogTitle(selectionType === 'invite' ? t('inviteNewUser') : selectionType === 'create' ? t('createNewUser') : isExisting ? user?.name || t('updateUser') : t('newRemoteUser'));
    setDialogOkayLabel(selectionType === 'invite' ? t('invite') : selectionType === 'create' ? t('create') : isExisting ? t('update') : t('add'));

    // Check if selected existing user
    if (isExisting) {
      this.getRoles(user);
      return this.setState({
        selectionType
      }, this.sendChange);
    }

    // Check if we need to add a default username. Tries to use email but if already in use will generate a uuid.
    if (selectionType === 'invite' && user.username == null) {
      const hasExistingUsers = Array.isArray(existingUsers) && existingUsers.length > 0;
      const existingUsernames = hasExistingUsers ? existingUsers.map(existingUser => existingUser.username) : [];
      user.username = existingUsernames.includes(user.email) ? v4() : user.email;
    }

    // Check if we need to add a default password.
    if (selectionType === 'invite') {
      user.password = `${v4()}2K!`;
    }

    // Update user data for all other selections
    this.setState({
      userId: null,
      user,
      originalRoles: [],
      roles: [],
      userExists: false,
      selectionType
    }, this.sendChange);
  };

  /**
   * Render component.
   */
  render() {
    // Get parameters
    const {
      projectId,
      roleSelector: RoleSelector = DefaultRoleSelector,
      selectorProps,
      canCreateUsers,
      edit = false,
      allowApiAccess = false,
      api
    } = this.props;
    const {
      user,
      roles,
      userExists,
      selectionType,
      newUserFormKey
    } = this.state;
    const showSelect = selectionType !== 'invite' && selectionType !== 'create';

    // Initialize role selector
    const roleSelector = /*#__PURE__*/React.createElement(RoleSelector, _extends({}, selectorProps, {
      projectId: projectId,
      user: user,
      value: roles,
      variants: selectorProps?.variants,
      roleFilter: selectorProps?.roleFilter,
      onChange: this.onRoleChange,
      requireRoles: true
    }));

    // Return component
    return /*#__PURE__*/React.createElement(Pane, {
      variant: selectorProps?.variants?.editorPane,
      className: S.editor
    }, showSelect && /*#__PURE__*/React.createElement(UserSelector, {
      projectId: projectId,
      onChange: this.onSelectUser,
      variants: selectorProps?.variants,
      roleFilter: selectorProps?.roleFilter,
      selectionType: selectionType,
      canCreateUsers: canCreateUsers,
      user: user,
      allowApiAccess: allowApiAccess,
      api: api,
      setExistingUsers: existingUsers => {
        this.setState({
          existingUsers
        });
      },
      requireEmail: userExists || selectionType === 'oidcAccess' || selectionType === 'apiAccess',
      requireUsername: userExists || selectionType === 'apiAccess',
      roleSelector: roleSelector,
      allowNewUsers: true,
      allowExternalUsers: true
    }), !showSelect && /*#__PURE__*/React.createElement(NewUserForm, {
      key: newUserFormKey,
      type: selectionType,
      variants: selectorProps?.variants,
      roleFilter: selectorProps?.roleFilter,
      user: user,
      roleSelector: roleSelector,
      onValueChange: this.onLocalUpdate
    }));
  }
}

/* Export and Connect Module */
export default connect()(AddEditUserEditor);